/**
 * AssetService
 * Main service for the asset registry: CRUD, views, transfers, status, tags, maintenance.
 */
import { CrudService } from '../base/CrudService';
/**
 * Service for asset CRUD plus the transfer / status / tag / maintenance actions.
 * CRUD (getAll/getById/create/update/delete) is inherited from CrudService.
 */
export class AssetService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? AssetService.DEFAULT_SERVICE_PATH,
            basePath: AssetService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ---- views ----
    /** Enriched asset view (resolved names, age, tags, current assignment). */
    async getView(id) {
        const response = await this.getViewRaw(id);
        if (!response.data)
            throw new Error(`Asset view with ID ${id} not found`);
        return response.data;
    }
    getViewRaw(id) {
        return this.http.get(this.buildUrl(`view/${id}`));
    }
    /** Filtered list of asset views. */
    async listView(filter) {
        const response = await this.listViewRaw(filter);
        return response.data ?? [];
    }
    listViewRaw(filter) {
        return this.http.post(this.buildUrl('list-view'), filter ?? {});
    }
    // ---- transfer / assignments ----
    /** Transfer the asset to a new owner / location / department (preserves history). */
    async transfer(id, req) {
        const response = await this.transferRaw(id, req);
        if (!response.data)
            throw new Error(`Failed to transfer asset ${id}`);
        return response.data;
    }
    transferRaw(id, req) {
        return this.http.post(this.buildUrl(`${id}/transfer`), req);
    }
    /** Acknowledge a pending transfer (assignee). */
    async acknowledgeTransfer(assignmentId, notes) {
        const response = await this.acknowledgeTransferRaw(assignmentId, notes);
        if (!response.data)
            throw new Error(`Failed to acknowledge transfer ${assignmentId}`);
        return response.data;
    }
    acknowledgeTransferRaw(assignmentId, notes) {
        return this.http.post(this.buildUrl(`transfers/${assignmentId}/acknowledge`), { notes });
    }
    /** The asset's full chain-of-custody (assignment history). */
    async getAssignments(id) {
        const response = await this.getAssignmentsRaw(id);
        return response.data ?? [];
    }
    getAssignmentsRaw(id) {
        return this.http.get(this.buildUrl(`${id}/assignments`));
    }
    // ---- status ----
    /** Change the asset status (writes a history row). */
    async changeStatus(id, req) {
        const response = await this.changeStatusRaw(id, req);
        if (!response.data)
            throw new Error(`Failed to change status for asset ${id}`);
        return response.data;
    }
    changeStatusRaw(id, req) {
        return this.http.post(this.buildUrl(`${id}/status`), req);
    }
    async getStatusHistory(id) {
        const response = await this.getStatusHistoryRaw(id);
        return response.data ?? [];
    }
    getStatusHistoryRaw(id) {
        return this.http.get(this.buildUrl(`${id}/status-history`));
    }
    // ---- tags ----
    async getTags(id) {
        const response = await this.getTagsRaw(id);
        return response.data ?? [];
    }
    getTagsRaw(id) {
        return this.http.get(this.buildUrl(`${id}/tags`));
    }
    async registerTag(id, req) {
        const response = await this.registerTagRaw(id, req);
        if (!response.data)
            throw new Error(`Failed to register tag for asset ${id}`);
        return response.data;
    }
    registerTagRaw(id, req) {
        return this.http.post(this.buildUrl(`${id}/tags`), req);
    }
    async deactivateTag(tagId) {
        const response = await this.http.delete(this.buildUrl(`tags/${tagId}`));
        return response.success;
    }
    // ---- maintenance ----
    async getMaintenance(id) {
        const response = await this.getMaintenanceRaw(id);
        return response.data ?? [];
    }
    getMaintenanceRaw(id) {
        return this.http.get(this.buildUrl(`${id}/maintenance`));
    }
    async logMaintenance(id, req) {
        const response = await this.logMaintenanceRaw(id, req);
        if (!response.data)
            throw new Error(`Failed to log maintenance for asset ${id}`);
        return response.data;
    }
    logMaintenanceRaw(id, req) {
        return this.http.post(this.buildUrl(`${id}/maintenance`), req);
    }
    // ---- bulk import ----
    /** Bulk-import a batch of assets (≤500); returns per-row results. */
    async bulkImport(req) {
        const response = await this.bulkImportRaw(req);
        if (!response.data)
            throw new Error('Bulk import failed');
        return response.data;
    }
    bulkImportRaw(req) {
        return this.http.post(this.buildUrl('bulk-import'), req);
    }
}
AssetService.DEFAULT_SERVICE_PATH = 'assetmanagement';
AssetService.BASE_PATH = 'api/assets';
//# sourceMappingURL=AssetService.js.map